'use client';
/** @jsxImportSource @emotion/react */

import type { AvatarGroupProps as MuiAvatarGroupProps } from '@mui/material/AvatarGroup';

import MuiAvatarGroup, { avatarGroupClasses } from '@mui/material/AvatarGroup';

import { avatarSize, type avatarSizes } from '../avatar/Avatar';
import { styled } from '../styled/styled';

/**
 * The component prop was removed from AvatarGroup because changing the underlying element could interfere with its intended layout and styling.
 * The AvatarGroup relies on a specific structure for positioning and sizing avatars, and modifying the element could break its expected behavior.
 * Keeping the default element ensures consistent design and functionality.
 *
 * And the default implementation in MUI is also broken, and altering it could lead to issues.
 *
 * https://serko.atlassian.net/browse/ZDS-1033
 */
type ExcludedMuiProps = 'component';

interface AvatarGroupProps extends Omit<MuiAvatarGroupProps, ExcludedMuiProps> {
  size?: avatarSizes;
}

export type { AvatarGroupProps };
export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  size = 'md',
  ...props
}) => {
  return <StyledMuiAvatarGroup size={size} {...props}></StyledMuiAvatarGroup>;
};

const StyledMuiAvatarGroup = styled(MuiAvatarGroup)<{ size: avatarSizes }>(
  ({ size, theme }) => ({
    [`.${avatarGroupClasses.avatar}`]: {
      backgroundColor: theme.palette.secondary.main,
      height: avatarSize[size],
      width: avatarSize[size],
    },
  })
);

AvatarGroup.displayName = 'AvatarGroup';
